import { z } from 'zod'
import type { Role } from './roles.ts'
import { type PaletteColor, paletteColorSchema } from './schemas.ts'

/**
 * Configurable lists (PRD "Settings"). Array order is meaningful: priorities lowest first,
 * statuses in board order. Nothing is ever hard-deleted; items are archived instead.
 */

/** Fixed workflow stages (PRD "Status workflow"). The stage, not the name, drives behaviour. */
export const STAGES = ['new', 'in_progress', 'waiting', 'done'] as const
export const stageSchema = z.enum(STAGES)
export type Stage = z.infer<typeof stageSchema>

export interface StageMeta {
  label: string
  /** Short explanation (tooltips). */
  description: string
  /** What the ETA clock does in this stage. */
  clock: 'running' | 'paused' | 'stopped'
}

export const STAGE_META: Record<Stage, StageMeta> = {
  new: {
    label: 'New',
    description: 'Not yet picked up, or came back after being resolved. The ETA clock runs.',
    clock: 'running',
  },
  in_progress: {
    label: 'In progress',
    description: 'HR owns the next step. The ETA clock runs.',
    clock: 'running',
  },
  waiting: {
    label: 'Waiting',
    description:
      'The employee owns the next step. The ETA clock is paused and the ticket is never overdue.',
    clock: 'paused',
  },
  done: {
    label: 'Done',
    description: 'No more work expected. The ETA clock stops and Resolved at is recorded.',
    clock: 'stopped',
  },
}

/** Stable string ids: lowercase slug, optionally with a short random suffix. */
export const configIdSchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]{0,63}$/, 'Ids are lowercase letters, digits and dashes')

export const MIN_SLA_DAYS = 0.5
export const MAX_SLA_DAYS = 365

/** Names may arrive empty: `validateConfig` turns that into a section-named message. */
const nameField = z.string().max(80)

export const categorySchema = z.object({
  id: configIdSchema,
  name: nameField,
  color: paletteColorSchema,
  /** Prompt shown to employees; becomes the description placeholder. */
  hint: z.string().max(500),
  archived: z.boolean(),
})
export type Category = z.infer<typeof categorySchema>

export const prioritySchema = z.object({
  id: configIdSchema,
  name: nameField,
  color: paletteColorSchema,
  /** Calendar days to resolve; sets the default ETA. Normalised to ≥ 0.5 in steps of 0.5. */
  slaDays: z.number().finite().max(MAX_SLA_DAYS),
  archived: z.boolean(),
})
export type Priority = z.infer<typeof prioritySchema>

export const statusSchema = z.object({
  id: configIdSchema,
  name: nameField,
  color: paletteColorSchema,
  stage: stageSchema,
  archived: z.boolean(),
})
export type Status = z.infer<typeof statusSchema>

export const configListsSchema = z.object({
  categories: z.array(categorySchema).max(200),
  priorities: z.array(prioritySchema).max(50),
  statuses: z.array(statusSchema).max(100),
})
export type ConfigLists = z.infer<typeof configListsSchema>

/** `GET /api/config` and the `PUT` response. Lists are ordered and include archived items. */
export const configSchema = configListsSchema.extend({
  /** Optimistic-concurrency token; send it back on `PUT`. */
  version: z.number().int().nonnegative(),
})
export type Config = z.infer<typeof configSchema>

/** `PUT /api/config` body: the full document plus the version it was edited from. */
export const configSaveSchema = configSchema.strict()
export type ConfigSave = z.infer<typeof configSaveSchema>

export const CONFIG_SECTIONS = ['categories', 'priorities', 'statuses', 'people'] as const
export type ConfigSection = (typeof CONFIG_SECTIONS)[number]

export const SECTION_LABELS: Record<ConfigSection, string> = {
  categories: 'Categories',
  priorities: 'Priorities',
  statuses: 'Statuses',
  people: 'People',
}

/** The person fields the save rules look at. */
export interface PersonDraft {
  id: string
  name: string
  role: Role
  isHrLead: boolean
  archived: boolean
}

export interface ConfigDraft extends ConfigLists {
  /** Optional: only checked when present (the Settings page sends it; `PUT /api/config` doesn't). */
  people?: PersonDraft[]
}

export interface ConfigError {
  section: ConfigSection
  message: string
}

export type ValidateResult<T extends ConfigDraft> =
  | { ok: true; value: T }
  | { ok: false; errors: ConfigError[] }

/** max(0.5, value or 1), rounded to the nearest 0.5. */
export function normaliseSlaDays(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value)
  const base = Number.isFinite(n) && n > 0 ? n : 1
  return Math.min(MAX_SLA_DAYS, Math.max(MIN_SLA_DAYS, Math.round(base * 2) / 2))
}

/**
 * PRD "Validation on save". Pure: returns either a normalised copy (names trimmed, SLA days
 * clamped to ≥ 0.5, HR lead cleared for non-HR people) or every failure, each naming its
 * section. Callers show the first error and save nothing.
 */
export function validateConfig<T extends ConfigDraft>(draft: T): ValidateResult<T> {
  const errors: ConfigError[] = []
  const label = (s: ConfigSection) => SECTION_LABELS[s]

  const categories = draft.categories.map((c) => ({ ...c, name: c.name.trim(), hint: c.hint }))
  const priorities = draft.priorities.map((p) => ({
    ...p,
    name: p.name.trim(),
    slaDays: normaliseSlaDays(p.slaDays),
  }))
  const statuses = draft.statuses.map((s) => ({ ...s, name: s.name.trim() }))
  const people = draft.people?.map((p) => ({
    ...p,
    name: p.name.trim(),
    isHrLead: p.role === 'admin' && p.isHrLead,
  }))

  const lists: [ConfigSection, { id: string; name: string; archived: boolean }[] | undefined][] = [
    ['categories', categories],
    ['priorities', priorities],
    ['statuses', statuses],
    ['people', people],
  ]
  for (const [section, items] of lists) {
    if (!items) continue
    if (items.some((i) => !i.name))
      errors.push({ section, message: `${label(section)}: every item needs a name.` })
    if (new Set(items.map((i) => i.id)).size !== items.length)
      errors.push({ section, message: `${label(section)}: two items share the same id.` })
    if (!items.some((i) => !i.archived))
      errors.push({ section, message: `${label(section)}: keep at least one active item.` })
  }

  if (!statuses.some((s) => !s.archived && s.stage === 'done'))
    errors.push({ section: 'statuses', message: 'Keep at least one status in the Done stage.' })
  if (people && !people.some((p) => !p.archived && p.role === 'admin'))
    errors.push({ section: 'people', message: 'Keep at least one active HR team member.' })

  if (errors.length > 0) return { ok: false, errors }
  return {
    ok: true,
    value: { ...draft, categories, priorities, statuses, ...(people ? { people } : {}) },
  }
}

/** Lowercase slug of a name (ASCII letters and digits, dashes between words). */
export function slugify(name: string): string {
  return (
    name
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'item'
  )
}

/** Id for a new item: name slug plus 4 random characters (DESIGN.md §3.3). */
export function newConfigId(name: string, random: () => number = Math.random): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
  let suffix = ''
  for (let i = 0; i < 4; i++) suffix += chars[Math.floor(random() * chars.length)]
  return `${slugify(name)}-${suffix}`
}

/** Ids present in `current` but missing from `next` (nothing can be deleted). */
export function missingIds(current: ConfigLists, next: ConfigLists): string[] {
  const out: string[] = []
  for (const key of ['categories', 'priorities', 'statuses'] as const) {
    const ids = new Set(next[key].map((i) => i.id))
    for (const item of current[key]) if (!ids.has(item.id)) out.push(`${key}/${item.id}`)
  }
  return out
}

// ---------------------------------------------------------------------------------------------
// Lookups for tickets (Wave 3+)

interface Item {
  id: string
  archived: boolean
}

/** Items that appear in pickers and new-ticket options. */
export function activeItems<T extends Item>(items: readonly T[]): T[] {
  return items.filter((i) => !i.archived)
}

export function findItem<T extends Item>(items: readonly T[], id: string | null | undefined) {
  return id ? items.find((i) => i.id === id) : undefined
}

/** Stage of a status id (undefined when unknown). */
export function stageOf(statuses: readonly Status[], statusId: string | null | undefined) {
  return findItem(statuses, statusId)?.stage
}

/** First active status in `stage`, in board order. */
export function firstActiveInStage(statuses: readonly Status[], stage: Stage): Status | undefined {
  return statuses.find((s) => !s.archived && s.stage === stage)
}

/** Fallbacks for references to unknown items (DESIGN.md §4). */
export const UNKNOWN_CATEGORY = { name: 'Uncategorised', color: 'slate' as PaletteColor }
export const UNKNOWN_PERSON_NAME = 'Former member'

// ---------------------------------------------------------------------------------------------
// Defaults (PRD "Appendix: default configuration")

export const DEFAULT_CONFIG: ConfigLists = {
  categories: [
    {
      id: 'reimbursement',
      name: 'Reimbursement',
      color: 'teal',
      hint: 'What was the expense, how much, and on what date? Attach the receipt.',
    },
    {
      id: 'leave',
      name: 'Leave',
      color: 'blue',
      hint: 'Which dates, what type of leave, and what’s the issue?',
    },
    {
      id: 'payroll',
      name: 'Payroll',
      color: 'violet',
      hint: 'Which month’s payslip, and what looks wrong?',
    },
    {
      id: 'office',
      name: 'Office & facilities',
      color: 'amber',
      hint: 'Where is the problem, and since when?',
    },
    {
      id: 'it',
      name: 'IT access',
      color: 'slate',
      hint: 'Which tool or system do you need access to, and why?',
    },
    {
      id: 'policy',
      name: 'Policy question',
      color: 'green',
      hint: 'Which policy, and what would you like to know?',
    },
    {
      id: 'grievance',
      name: 'Grievance',
      color: 'rose',
      hint: 'Tell us what happened. You can mark this ticket confidential.',
    },
    { id: 'other', name: 'Other', color: 'slate', hint: 'Describe what you need help with.' },
  ].map((c) => ({ ...c, color: c.color as PaletteColor, archived: false })),
  priorities: [
    { id: 'low', name: 'Low', color: 'slate', slaDays: 7 },
    { id: 'medium', name: 'Medium', color: 'blue', slaDays: 3 },
    { id: 'high', name: 'High', color: 'orange', slaDays: 2 },
    { id: 'urgent', name: 'Urgent', color: 'rose', slaDays: 1 },
  ].map((p) => ({ ...p, color: p.color as PaletteColor, archived: false })),
  statuses: [
    { id: 'open', name: 'Open', color: 'slate', stage: 'new' },
    { id: 'reopened', name: 'Reopened', color: 'orange', stage: 'new' },
    { id: 'ack', name: 'Acknowledged', color: 'blue', stage: 'in_progress' },
    { id: 'progress', name: 'In progress', color: 'violet', stage: 'in_progress' },
    { id: 'waiting', name: 'Waiting on employee', color: 'amber', stage: 'waiting' },
    { id: 'resolved', name: 'Resolved', color: 'green', stage: 'done' },
    { id: 'closed', name: 'Closed', color: 'slate', stage: 'done' },
  ].map((s) => ({
    ...s,
    color: s.color as PaletteColor,
    stage: s.stage as Stage,
    archived: false,
  })),
}

/** Defaults for rows created with "+ Add …" (DESIGN.md §3.3). */
export const NEW_ITEM_DEFAULTS = {
  category: { name: 'New category', color: 'slate', hint: '' },
  priority: { name: 'New priority', color: 'slate', slaDays: 3 },
  status: { name: 'New status', color: 'slate', stage: 'in_progress' },
} as const
